import { Box, Button, IconButton, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { ArrowLeft, Copy, Lock, X } from "react-feather";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TreffasSwicth from "src/components/TreffasSwitch";
import TreffasTextField from "src/components/TreffasTextField";
import { PlanSubscription } from "src/context";
import { useSnackbarContext } from "src/context/SnackbarContext";
import { buildSignedEmbedUrl } from "src/utils/embedToken";
import TreffasStyle from "src/utils/TreffasStyle";
import TreffasButton from "src/components/TreffasButton";

const Embed = ({ bookingURL, close, onChangeCategory, hasHeaderOption }) => {
  const { t } = useTranslation();
  const [type, setType] = React.useState("script");

  const { showSnackbar } = useSnackbarContext();
  const [removes, setRemoves] = React.useState({
    header: false,
    footer: false,
  });

  const { planSubscription } = useContext(PlanSubscription);
  const navigate = useNavigate();

  const [withLogo, setWithLogo] = React.useState(true);
  const [embedURL, setEmbedURL] = useState("");
  const [isSigning, setIsSigning] = useState(false);

  const baseUrl = window.location.origin;
  const hasEmbed = planSubscription?.inclusive?.package?.embed ?? false;

  useEffect(() => {
    if (!hasEmbed) {
      setEmbedURL("");
      return;
    }

    let cancelled = false;
    setIsSigning(true);

    const options = hasHeaderOption
      ? { hide_header: removes.header, hide_footer: removes.footer }
      : { hide_header: false, hide_footer: withLogo };

    buildSignedEmbedUrl(bookingURL, options)
      .then((url) => {
        if (!cancelled) {
          setEmbedURL(url);
        }
      })
      .catch((error) => {
        console.log("embed sign error:", error);
        if (!cancelled) {
          setEmbedURL("");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSigning(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bookingURL, hasEmbed, hasHeaderOption, removes.footer, removes.header, withLogo]);

  const embedScript = embedURL
    ? `<script src="${baseUrl}/js/embed.js" data-url="${embedURL}"> </script>`
    : "";
  const embedIframe = embedURL
    ? `<iframe width="100%" height="100%" src="${embedURL}" title="Packages List"> </iframe>`
    : "";

  return (
    <Box>
      <Box
        sx={{
          p: "20px",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <IconButton onClick={() => onChangeCategory("booking")}>
              <ArrowLeft size={20} />
            </IconButton>
          </Box>

          <Box>
            <IconButton onClick={() => close()}>
              <X size={20} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ mt: "4px" }}>
          <Typography
            sx={{
              fontFamily: "var(--font-grenettepromedium)",
              fontStyle: "normal",
              fontSize: "26px",
              lineHeight: "26px",
              letterSpacing: "-0.01em",
              color: "#000000",
              textAlign: "center",
            }}
          >
            {t("package.label.booking.embed_on_your_website")}
          </Typography>
        </Box>

        <Box sx={{ mt: "8px" }}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "18px",
              textAlign: "center",
              color: "#6E757C",
            }}
          >
            {t("package.label.booking.embed_on_your_website.description")}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          borderBottom: "1px solid #EAECEF",
          display: "flex",
          justifyContent: "center",
          gap: "24px",
        }}
      >
        <Box
          sx={{
            py: "17.5px",
            ...(type === "script" && {
              borderBottom: "1px solid #D97054",
            }),
            cursor: "pointer",
          }}
          onClick={() => setType("script")}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "normal",
              color: type === "script" ? "#1D1B1B" : "#697077",
            }}
          >
            Script
          </Typography>
        </Box>

        <Box
          sx={{
            py: "17.5px",
            ...(type === "iframe" && {
              borderBottom: "1px solid #D97054",
            }),
            cursor: "pointer",
          }}
          onClick={() => setType("iframe")}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: "normal",
              color: type === "iframe" ? "#1D1B1B" : "#697077",
            }}
          >
            Iframe
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: "40px",
          mt: "32px",
        }}
      >
        <Box>
          <TreffasTextField
            InputProps={{ readOnly: true }}
            value={
              isSigning
                ? t("package.label.booking.embed_on_your_website")
                : type === "script"
                ? embedScript
                : embedIframe
            }
            fullWidth
            sx={{
              ...TreffasStyle.formInputLarge("textarea"),
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px !important",
                backgroundColor: "#F4F5F6",
                minHeight: "100%",
              },
              "& .MuiInputBase-input": {
                fontWeight: 400,
                fontSize: "13px",
                color: "#353A3F",
              },
            }}
            multiline
            minRows={3}
          />
        </Box>
      </Box>

      <Box
        sx={{
          px: "40px",
          mt: "16px",
        }}
      >
        {hasHeaderOption ? (
          <Box>
            <Box
              sx={{
                mb: "10px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-grenettepromedium)",
                  fontStyle: "normal",
                  fontSize: "11px",
                  lineHeight: "14px",
                  letterSpacing: "0.07em",
                  color: "#6E757C",
                  textTransform: "uppercase",
                }}
              >
                {t("package.label.booking.embed_options")}
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: "#F4F5F6",
                borderRadius: "8px",
              }}
            >
              <Box
                sx={{
                  height: "42px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: "8px 16px",
                  borderBottom: "1px solid #EAECEF",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "18px",
                      color: "#000000",
                      textTransform: "capitalize",
                    }}
                  >
                    {t("package.label.booking.header")}
                  </Typography>
                </Box>
                <Box>
                  <TreffasSwicth
                    checked={removes.header}
                    onChange={(e) =>
                      setRemoves({ ...removes, header: e.target.checked })
                    }
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  height: "42px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: "8px 16px",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "18px",
                      color: "#000000",
                      textTransform: "capitalize",
                    }}
                  >
                    {t("package.label.booking.footer")}
                  </Typography>
                </Box>
                <Box>
                  <TreffasSwicth
                    checked={removes.footer}
                    onChange={(e) =>
                      setRemoves({ ...removes, footer: e.target.checked })
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: "42px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: "8px 16px",
              backgroundColor: "#F4F5F6",
              borderRadius: "8px",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "18px",
                  textTransform: "capitalize",
                }}
              >
                {t("package.label.booking.remove_treffas_logo")}
              </Typography>
            </Box>
            <Box>
              <TreffasSwicth
                checked={withLogo}
                onChange={(e) => setWithLogo(e.target.checked)}
              />
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          px: "40px",
          mt: "32px",
          pb: "40px",
        }}
      >
        <CopyToClipboard
          text={type === "script" ? embedScript : embedIframe}
          onCopy={() =>
            showSnackbar(
              t("individual.booking.embed_code.copy.clipboard"),
              "success"
            )
          }
        >
          <TreffasButton
            variant="primary" size="md"
            fullWidth
            sx={{
              minHeight: "44px",
              fontFamily: "var(--font-grenettepromedium)",
              fontSize: "15px",
              lineHeight: "20px",
              borderRadius: "8px",
              }}
            startIcon={hasEmbed ? <Copy size={18} /> : <Lock size={18} />}
            disabled={!hasEmbed || isSigning || !embedURL}
          >
            {t("package.button.copy_embed_code")}
          </TreffasButton>
        </CopyToClipboard>

        {!hasEmbed && (
          <Box sx={{ mt: "16px" }}>
            <Typography
              sx={{
                color: "#6E757C",
                textAlign: "center",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 400,
                lineHeight: "normal",
              }}
            >
              Access this feature with{" "}
              <Typography
                variant={"caption"}
                sx={{
                  color: "var(--color-brand-primary)",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "normal",
                  textDecorationLine: "underline",
                  cursor: "pointer",
                }}
                onClick={() => navigate(`/app/billings/plans/upgrade`)}
              >
                Treffas Pro
              </Typography>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Embed;
